import React, { useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useParams, useLocation } from 'react-router-dom'
import { Typography, MenuItem } from '@material-ui/core'
import { AddOutlined, FilterList, PhoneAndroid } from '@material-ui/icons'
import { gray, purple } from '../../../utils/colors'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { MainButton } from '../../../components'
import usePromotions from '../../../hooks/usePromotions'
import { MTableCell } from '@material-table/core'
import StyledTable from '../../../components/Common/StyledTable'
import StyledChip from '../../../components/Common/StyledChip'
import PromoDialog from '../../../components/Dialogs/Promos/PromoDialog'
import {
	formatNumsToDollars,
	PROMO_STATUSES,
	PROMO_TEMPLATE,
	PROMO_SERVICE_TARGET_OPTIONS,
	DIALOG_EDIT_MODES,
	standardizeEndOfDateUTC,
	standardizeStartOfDateUTC,
	CLIENT_APP_PROMO_LIMIT
} from '../../../utils'
import useCurrentUser from '../../../hooks/useCurrentUser'
import MoreMenu from '../../../components/Common/Menus/MoreMenu'
import DangerConfirmationDialog from '../../../components/Common/Dialogs/DangerConfirmation'
import moment from 'moment'

const useStyles = makeStyles((theme) => ({
	container: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column'
	},
	header: {
		display: 'flex',
		flex: 1,
		padding: '1.6rem 1.5rem',
		alignItems: 'center',
		gap: '1rem',
		borderBottom: `0.0625rem solid ${gray[500]}`
	},
	secondaryTools: {
		padding: '1.2rem 1.5rem',
		display: 'flex',
		borderBottom: `0.0625rem solid ${gray[500]}`
	},
	title: { fontSize: 22, color: purple[900] },
	content: {
		display: 'flex',
		flex: 1,
		overflow: 'scroll'
	}
}))

const renderAmount = (data) => {
	const { unit, value } = data
	if (unit === 'percent') {
		return `${value}%`
	}
	if (unit === 'cents') {
		return formatNumsToDollars(value)
	}
}

const Promos = () => {
	const classes = useStyles()
	const theme = useTheme()

	const [promoEditMode, setPromoEditMode] = useState(DIALOG_EDIT_MODES.New)
	const [promoEditDialogOpen, setPromoEditDialogOpen] = useState(false)
	const [currentPromoBeingEdited, setCurrentPromoBeingEdited] = useState(PROMO_TEMPLATE)
	const [confirmDeletePromoDialogOpen, setConfirmDeletePromoDialogOpen] = useState(false)
	const [promoIdToDelete, setPromoIdToDelete] = useState('')

	const params = useParams()
	const location = useLocation()
	const { promotions, getPromos, promosBusy, updatePromo } = usePromotions()
	const { user, isSuperUser, facilityId: assignedFacilityId } = useCurrentUser()
	const facilities = useSelector((state) => state.specific.facilities.facilities || [])
	const isFacilityScoped = Boolean(params.facilityId) && !location.pathname.includes('/globalAdmin')
	const allowMultipleFacilities = isSuperUser && !isFacilityScoped
	const lockToFacilityId = isFacilityScoped ? params.facilityId : allowMultipleFacilities ? null : assignedFacilityId

	useEffect(() => {
		const getPromosAsync = async () => {
			getPromos({ facilityId: isFacilityScoped ? params.facilityId : null })
		}
		getPromosAsync()
	}, [isFacilityScoped, params.facilityId])

	// Count active promos with show_in_client_app enabled
	const activeClientAppPromosCount = useMemo(() => {
		if (!promotions || !Array.isArray(promotions)) {
			return 0
		}

		const nowUTC = moment().utc()

		return promotions.filter((p) => {
			// Must have show_in_client_app enabled
			if (!p.show_in_client_app) {
				return false
			}

			// Must not be deactivated
			if (p.deactivated) {
				return false
			}

			// Must be active (not past end date)
			const endDate = standardizeEndOfDateUTC(p.end_date)

			// Check if promo is currently active (not past end date)
			if (nowUTC.isAfter(endDate)) {
				return false
			}

			// Promo is active if it's scheduled (before start) or running (between start and end)
			const startDate = standardizeStartOfDateUTC(p.start_date)
			return nowUTC.isBefore(startDate) || nowUTC.isBetween(startDate, endDate, null, '[]')
		}).length
	}, [promotions])

	const handlePromoDialogOpen = ({ editMode = DIALOG_EDIT_MODES.New }) => {
		setPromoEditMode(editMode)
		setPromoEditDialogOpen(true)
	}

	const handlePromoDialogClose = () => {
		setPromoEditDialogOpen(false)
	}

	const handleDeletePromoDialogOpen = (e) => {
		setPromoIdToDelete(e._id)
		setConfirmDeletePromoDialogOpen(true)
	}

	const handleDeletePromoDialogClose = () => {
		setPromoIdToDelete('')
		setConfirmDeletePromoDialogOpen(false)
	}

	const handleNewPromoButtonClick = () => {
		setCurrentPromoBeingEdited({
			...PROMO_TEMPLATE,
			created_by: user.uid,
			applicable_facilities: lockToFacilityId ? [lockToFacilityId] : []
		})
		handlePromoDialogOpen({ editMode: DIALOG_EDIT_MODES.New })
	}

	const handleDuplicatePromoButtonClick = (existingPromo) => {
		const duplicatePromo = {}

		Object.keys(PROMO_TEMPLATE).forEach((key) => {
			duplicatePromo[key] = existingPromo[key]
		})

		duplicatePromo.start_date = null
		duplicatePromo.end_date = null
		duplicatePromo.start_time = null
		duplicatePromo.end_time = null
		duplicatePromo.created_by = user.uid
		duplicatePromo.status = PROMO_STATUSES.Draft
		if (lockToFacilityId) {
			duplicatePromo.applicable_facilities = [lockToFacilityId]
		}

		setCurrentPromoBeingEdited(duplicatePromo)
		handlePromoDialogOpen({ editMode: DIALOG_EDIT_MODES.New })
	}

	const handleEditPromoButtonClick = (existingPromo) => {
		setCurrentPromoBeingEdited({ ...existingPromo })
		handlePromoDialogOpen({ editMode: DIALOG_EDIT_MODES.Edit })
	}

	const handleViewPromoButtonClicked = (existingPromo) => {
		setCurrentPromoBeingEdited({ ...existingPromo })
		handlePromoDialogOpen({ editMode: DIALOG_EDIT_MODES.ViewOnly })
	}

	const handleDeletePromo = async () => {
		await updatePromo({ updates: { deleted: true, deleted_by: user.uid }, promo_id: promoIdToDelete })
		handleDeletePromoDialogClose()
	}

	const columns = [
		{
			title: 'Name',
			field: 'name',
			searchable: true,
			render: (data) => {
				const nowUTC = moment().utc()
				const endDate = standardizeEndOfDateUTC(data.end_date)
				const isNotPastEndDate = nowUTC.isBefore(endDate) || nowUTC.isSame(endDate, 'day')
				const shouldShowIcon =
					data.show_in_client_app &&
					!data.deactivated &&
					isNotPastEndDate

				return (
					<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
						<span>{data.name}</span>
						{shouldShowIcon && (
							<PhoneAndroid
								fontSize="small"
								style={{ color: purple[600], fill: purple[600] }}
								titleAccess="Visible in client app"
							/>
						)}
					</div>
				)
			}
		},
		{
			title: 'Code',
			field: 'code',
			searchable: true
		},
		{
			title: 'Amount',
			field: 'value',
			render: (data) => renderAmount(data),
			searchable: false
		},
		{
			title: 'Facility(s)',
			sorting: false,
			render: (data) =>
				data.applicable_facilities?.length === 0
					? 'All'
					: data.applicable_facilities
						?.map((_id) => {
							const facility = facilities.find((item) => String(item._id) === String(_id))
							return facility?.name || '?'
						})
						.join(', '),
			searchable: false
		},
		{
			title: 'Service(s)',
			sorting: false,
			render: (data) => {
				if (data.applicable_services?.length === 0) {
					return 'All'
				}
				const serviceNames = data.applicable_services.map((service) => {
					return PROMO_SERVICE_TARGET_OPTIONS[service]
				})
				return serviceNames.join(', ')
			},
			searchable: false
		},
		{
			title: 'Start Date',
			field: 'start_date',
			render: (data) => standardizeStartOfDateUTC(data.start_date).format('MMM DD, YYYY'),
			searchable: false
		},
		{
			title: 'End Date',
			field: 'end_date',
			render: (data) => standardizeEndOfDateUTC(data.end_date).format('MMM DD, YYYY'),
			searchable: false
		},
		{
			title: 'Redeem',
			field: 'redeems',
			width: 50,
			searchable: false
		},
		{
			// status is a derived field and is created in promotions.actions from api data
			title: 'Status',
			field: 'status',
			render: (data) => {
				let variant = 'purple'
				if (
					data.status === PROMO_STATUSES.Draft ||
					data.status === PROMO_STATUSES.Completed ||
					data.status === PROMO_STATUSES.Deactivated
				) {
					variant = 'gray'
				}
				if (data.status === PROMO_STATUSES.Running) {
					variant = 'successes'
				}
				if (data.status === PROMO_STATUSES.Scheduled) {
					variant = 'purple'
				}
				if (data.status === PROMO_STATUSES.Paused) {
					variant = 'warnings'
				}
				return <StyledChip skiptownvariant={variant} label={data.status} />
			},
			searchable: false
		},
		{
			title: '',
			width: 50,
			sorting: false,
			searchable: false,
			render: (d) => {
				const menuOptions = []
				if (![PROMO_STATUSES.Completed, PROMO_STATUSES.Deactivated].includes(d.status)) {
					menuOptions.push(
						<MenuItem key="edit-promo" onClick={() => handleEditPromoButtonClick(d)}>
							Edit Promo
						</MenuItem>
					)
				}

				menuOptions.push(
					<MenuItem key="dup-promo" onClick={() => handleDuplicatePromoButtonClick(d)}>
						Duplicate Promo
					</MenuItem>
				)

				if (d.status !== PROMO_STATUSES.Running) {
					menuOptions.push(
						<MenuItem key="delete-promo" onClick={() => handleDeletePromoDialogOpen(d)}>
							Delete Promo
						</MenuItem>
					)
				}

				menuOptions.push(
					<MenuItem key="view-promo" onClick={() => handleViewPromoButtonClicked(d)}>
						View Promo
					</MenuItem>
				)

				return <MoreMenu options={menuOptions} description={d.name} />
			}
		}
	]

	return (
		<>
			<PromoDialog
				open={promoEditDialogOpen}
				onClose={handlePromoDialogClose}
				onSaved={handlePromoDialogClose}
				onCancelled={handlePromoDialogClose}
				title={
					promoEditMode === DIALOG_EDIT_MODES.New
						? 'New Promo'
						: promoEditMode === DIALOG_EDIT_MODES.Edit
							? 'Edit Promo'
							: 'View Promo'
				}
				promo={currentPromoBeingEdited}
				mode={promoEditMode}
				facilities={facilities}
				lockToFacilityId={lockToFacilityId}
				allowMultipleFacilities={allowMultipleFacilities}
			/>
			<div className={classes.container}>
				<div className={classes.header}>
					<Typography variant="h3" className={classes.title}>
						Promotions
					</Typography>
					{activeClientAppPromosCount > 0 && (
						<Typography variant="body2" style={{ color: gray[600], marginLeft: '1rem' }}>
							{activeClientAppPromosCount} of {CLIENT_APP_PROMO_LIMIT} active promos displayed
							in client app
						</Typography>
					)}
					{/* <div>search</div>
					<div>controls</div> */}
					<div>
						<MainButton
							label="Promo"
							onClick={handleNewPromoButtonClick}
							startIcon={<AddOutlined style={{ fill: theme.palette.secondary.main }} />}
						/>
					</div>
				</div>
				{/* <div className={classes.secondaryTools}>secondary bar</div> */}
				<div className={classes.content}>
					<StyledTable
						icons={{
							Search: () => (
								<FilterList fontSize="small" style={{ color: gray[700], fill: gray[700] }} />
							)
						}}
						localization={{ toolbar: { searchPlaceholder: 'Filter' } }}
						options={{
							showTitle: false,
							toolbar: true,
							searchFieldAlignment: 'right',
							search: true,
							searchFieldVariant: 'outlined',
							searchFieldStyle: {
								height: '40px',
								paddingRight: 0
							},
							headerStyle: {
								color: gray[900],
								textTransform: 'uppercase',
								fontWeight: 800,
								fontSize: '12px',
								lineHeight: '16px',
								padding: '11px 24px',
								borderTop: `1px solid ${gray[500]}`
							},
							pageSize: 20,
							pageSizeOptions: [10, 20, 50],
							toolbarButtonAlignment: 'left'
						}}
						isLoading={promosBusy}
						columns={columns}
						data={promotions}
						onRowClick={() => null}
						components={{
							Cell: (props) => {
								return (
									<MTableCell
										style={{
											borderBottom: 'none',
											padding: '16px 36px 16px 24px'
										}}
										{...props}
									/>
								)
							}
						}}
					/>
				</div>
			</div>
			<DangerConfirmationDialog
				open={confirmDeletePromoDialogOpen}
				description="Are you sure you want to delete this promotion? Ending it will result in immediate deactivation and the promotion will no longer appear in this list."
				dangerButtonText="Delete Promo"
				onConfirmDangerousAction={handleDeletePromo}
				onCancel={handleDeletePromoDialogClose}
			/>
		</>
	)
}

export default Promos
